import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'

/* ───────────────────────── helpers ───────────────────────── */

function getInfoRFC(rfc) {
    return ElementsDataService.retrieveElement('contabilidad/refreshDatabaseTotals', rfc)
}

/* ───────────────────────── design tokens ────────────────────── */

const t = {
    bg: '#f1f5f9',
    card: '#ffffff',
    border: '#e8edf5',
    blue: '#3b82f6',
    blueHover: '#2563eb',
    bluePale: '#eff6ff',
    navy: '#0f172a',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    success: '#10b981',
    successBg: '#ecfdf5',
    successBorder: '#a7f3d0',
    danger: '#ef4444',
    dangerBg: '#fef2f2',
    dangerBorder: '#fecaca',
    warningBg: '#fffbeb',
    warningBorder: '#fde68a',
    radius: 16,
    radiusSm: 10,
    shadow: '0 1px 3px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04)',
    font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
}

/* ───────────────────────── Section ──────────────────────────── */

function Section({ title, subtitle, icon, action, children, style }) {
    return (
        <div
            style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: t.radius,
                boxShadow: t.shadow,
                ...style,
            }}
        >
            {(title || subtitle) && (
                <div
                    style={{
                        padding: '20px 24px',
                        borderBottom: `1px solid ${t.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {icon && (
                            <span
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: `${t.blue}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 17,
                                    flexShrink: 0,
                                }}
                            >
                                {icon}
                            </span>
                        )}
                        <div>
                            {title && (
                                <h2
                                    style={{
                                        margin: 0,
                                        fontSize: 16,
                                        fontWeight: 650,
                                        color: t.textPrimary,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {title}
                                </h2>
                            )}
                            {subtitle && (
                                <p style={{ margin: '2px 0 0', fontSize: 13, color: t.textSecondary }}>
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                    {action}
                </div>
            )}
            {children}
        </div>
    )
}

/* ───────────────────────── ProgressBar ──────────────────────── */

function ProgressBar({ current, total }) {
    if (total === 0) return null
    const pct = Math.round((current / total) * 100)
    return (
        <div style={{ padding: '16px 24px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: t.textPrimary }}>
                    Progreso
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: t.blue }}>
                    {current} / {total} — {pct}%
                </span>
            </div>
            <div
                style={{
                    height: 6,
                    borderRadius: 3,
                    background: '#e8edf5',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${pct}%`,
                        borderRadius: 3,
                        background: `linear-gradient(90deg, ${t.blue}, ${t.blueHover})`,
                        transition: 'width .35s ease',
                    }}
                />
            </div>
        </div>
    )
}

/* ───────────────────────── SociedadRow ──────────────────────── */

const STATUS_CONFIG = {
    pending: { icon: '○', color: t.textMuted, bg: 'transparent' },
    processing: { icon: '◌', color: t.blue, bg: t.bluePale },
    success: { icon: '✓', color: t.success, bg: t.successBg },
    error: { icon: '✕', color: t.danger, bg: t.dangerBg },
}

function SociedadRow({ entidad, status, index, total }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending
    const isActive = status === 'processing'

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 24px',
                background: cfg.bg,
                borderBottom: `1px solid ${t.border}`,
                transition: 'background .2s ease',
            }}
        >
            {/* Status icon */}
            <span
                className={isActive ? 'zenyth-spin' : undefined}
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    color: cfg.color,
                    flexShrink: 0,
                }}
            >
                {cfg.icon}
            </span>

            {/* Index */}
            <span
                style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: t.textMuted,
                    minWidth: 32,
                    flexShrink: 0,
                }}
            >
                {index + 1}/{total}
            </span>

            {/* Name */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 13.5,
                        fontWeight: 550,
                        color: t.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {entidad.nombre}
                </div>
            </div>

            {/* RFC */}
            <span
                style={{
                    fontSize: 12.5,
                    fontFamily: 'monospace',
                    color: t.textSecondary,
                    fontWeight: 500,
                    flexShrink: 0,
                    letterSpacing: '0.02em',
                }}
            >
                {entidad.rfc}
            </span>
        </div>
    )
}

/* ───────────────────────── Stat card ────────────────────────── */

function StatCard({ label, value, color, icon }) {
    return (
        <div
            style={{
                flex: 1,
                minWidth: 120,
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: t.radiusSm,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
            }}
        >
            <span style={{ fontSize: 20 }}>{icon}</span>
            <div>
                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: color || t.textPrimary,
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {value}
                </div>
                <div style={{ fontSize: 11.5, color: t.textMuted, fontWeight: 500, marginTop: 2 }}>
                    {label}
                </div>
            </div>
        </div>
    )
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */

function CfdiRefreshDashboardComponent() {
    const history = useHistory()
    const { id } = useParams()

    const [entidades, setEntidades] = useState([])
    const [statuses, setStatuses] = useState({})
    const [processing, setProcessing] = useState(false)
    const [processed, setProcessed] = useState(0)
    const [summary, setSummary] = useState(null)
    const [loadingEntidades, setLoadingEntidades] = useState(true)

    const cancelledRef = useRef(false)
    const listRef = useRef(null)

    /* ── auth guard + load sociedades ── */
    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('WRITE_CFDIS')) {
            alert('Usuario no autorizado')
            history.push('/login')
            return
        }

        ElementsDataService.retrieveAllElements('sociedades')
            .then((response) => {
                const arr = response.data.map((el) => ({
                    id: el.id,
                    nombre: el.razonSocial,
                    rfc: el.rfc,
                    tipo: 'sociedad',
                }))
                setEntidades(arr)

                const initial = {}
                arr.forEach((_, i) => {
                    initial[i] = 'pending'
                })
                setStatuses(initial)
                setLoadingEntidades(false)
            })
            .catch((err) => {
                console.error('Error loading sociedades:', err)
                setLoadingEntidades(false)
            })

        return () => {
            cancelledRef.current = true
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /* ── auto-scroll to active row ── */
    const scrollToActive = useCallback((idx) => {
        if (!listRef.current) return
        const rows = listRef.current.children
        if (rows[idx]) {
            rows[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [])

    /* ── process all ── */
    const handleStart = async () => {
        if (entidades.length === 0) return

        cancelledRef.current = false
        setProcessing(true)
        setProcessed(0)
        setSummary(null)

        /* reset statuses */
        const reset = {}
        entidades.forEach((_, i) => {
            reset[i] = 'pending'
        })
        setStatuses(reset)

        let successes = 0
        const failures = []

        for (let i = 0; i < entidades.length; i++) {
            if (cancelledRef.current) break

            setStatuses((prev) => ({ ...prev, [i]: 'processing' }))
            scrollToActive(i)

            try {
                const response = await getInfoRFC(entidades[i].rfc)

                if (response.status === 200) {
                    successes++
                    setStatuses((prev) => ({ ...prev, [i]: 'success' }))
                } else {
                    failures.push(entidades[i].nombre)
                    setStatuses((prev) => ({ ...prev, [i]: 'error' }))
                }
            } catch (err) {
                console.error(`Error refreshing ${entidades[i].rfc}:`, err)
                failures.push(entidades[i].nombre)
                setStatuses((prev) => ({ ...prev, [i]: 'error' }))
            }

            setProcessed(i + 1)
        }

        setProcessing(false)
        if (!cancelledRef.current) {
            setSummary({ successes, failures })
        }
    }

    const handleCancel = () => {
        cancelledRef.current = true
        setProcessing(false)
    }

    /* ── computed ── */
    const successCount = Object.values(statuses).filter((s) => s === 'success').length
    const errorCount = Object.values(statuses).filter((s) => s === 'error').length

    /* ────────────────── RENDER ────────────────── */
    return (
        <div
            style={{
                minHeight: '100vh',
                background: t.bg,
                fontFamily: t.font,
                padding: '32px 32px 64px',
            }}
        >
            <style>{`
                @keyframes zenyth-spin-kf { to { transform: rotate(360deg); } }
                .zenyth-spin { animation: zenyth-spin-kf .8s linear infinite; display: inline-block; }
            `}</style>

            <div style={{ maxWidth: 760, margin: '0 auto' }}>

                {/* Header */}
                <div style={{ marginBottom: 28 }}>
                    <button
                        onClick={() => history.goBack()}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'none',
                            border: 'none',
                            color: t.textSecondary,
                            fontSize: 13,
                            fontWeight: 550,
                            fontFamily: t.font,
                            cursor: 'pointer',
                            padding: 0,
                            marginBottom: 12,
                        }}
                    >
                        ← Regresar
                    </button>
                    <h1
                        style={{
                            margin: 0,
                            fontSize: 26,
                            fontWeight: 700,
                            color: t.navy,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Refrescar Dashboard Contable
                    </h1>
                    <p style={{ margin: '4px 0 0', fontSize: 14, color: t.textSecondary }}>
                        Actualiza los totales de contabilidad para todas las sociedades
                    </p>
                </div>

                {/* ── Stats row ── */}
                {processed > 0 && (
                    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                        <StatCard label="Total" value={entidades.length} icon="🏢" />
                        <StatCard label="Procesadas" value={processed} icon="⟳" color={t.blue} />
                        <StatCard label="Exitosas" value={successCount} icon="✓" color={t.success} />
                        {errorCount > 0 && (
                            <StatCard label="Con error" value={errorCount} icon="✕" color={t.danger} />
                        )}
                    </div>
                )}

                {/* ── Sociedades list ── */}
                <Section
                    title="Sociedades"
                    subtitle={
                        loadingEntidades
                            ? 'Cargando...'
                            : `${entidades.length} sociedad${entidades.length !== 1 ? 'es' : ''} registrada${entidades.length !== 1 ? 's' : ''}`
                    }
                    icon="🏢"
                    action={
                        !loadingEntidades && entidades.length > 0 && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                {processing && (
                                    <button
                                        onClick={handleCancel}
                                        style={{
                                            padding: '8px 16px',
                                            background: t.card,
                                            border: `1px solid ${t.dangerBorder}`,
                                            borderRadius: 8,
                                            color: t.danger,
                                            fontSize: 13,
                                            fontWeight: 550,
                                            fontFamily: t.font,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                )}
                                <button
                                    onClick={handleStart}
                                    disabled={processing}
                                    style={{
                                        padding: '8px 18px',
                                        background: processing ? '#cbd5e1' : t.blue,
                                        border: 'none',
                                        borderRadius: 8,
                                        color: '#fff',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        fontFamily: t.font,
                                        cursor: processing ? 'not-allowed' : 'pointer',
                                        transition: 'all .15s',
                                        boxShadow: !processing ? '0 2px 8px rgba(59,130,246,.25)' : 'none',
                                    }}
                                >
                                    {processing
                                        ? `Procesando ${processed}/${entidades.length}...`
                                        : summary
                                        ? '↻ Ejecutar de nuevo'
                                        : '▶ Iniciar actualización'}
                                </button>
                            </div>
                        )
                    }
                >
                    {/* Progress */}
                    {processing && <ProgressBar current={processed} total={entidades.length} />}

                    {/* Loading skeleton */}
                    {loadingEntidades && (
                        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                            <span className="zenyth-spin" style={{ fontSize: 20, color: t.blue }}>
                                ◌
                            </span>
                            <p style={{ margin: '10px 0 0', fontSize: 13, color: t.textMuted }}>
                                Cargando sociedades...
                            </p>
                        </div>
                    )}

                    {/* Empty */}
                    {!loadingEntidades && entidades.length === 0 && (
                        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                            <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }}>🏢</div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: t.textPrimary }}>
                                Sin sociedades
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: t.textSecondary }}>
                                No se encontraron sociedades registradas
                            </p>
                        </div>
                    )}

                    {/* List */}
                    {!loadingEntidades && entidades.length > 0 && (
                        <div
                            ref={listRef}
                            style={{
                                maxHeight: 420,
                                overflowY: 'auto',
                            }}
                        >
                            {entidades.map((ent, idx) => (
                                <SociedadRow
                                    key={ent.id}
                                    entidad={ent}
                                    status={statuses[idx] || 'pending'}
                                    index={idx}
                                    total={entidades.length}
                                />
                            ))}
                        </div>
                    )}
                </Section>

                {/* ── Summary ── */}
                {summary && (
                    <div
                        style={{
                            marginTop: 16,
                            background: summary.failures.length === 0 ? t.successBg : t.warningBg,
                            border: `1px solid ${
                                summary.failures.length === 0 ? t.successBorder : t.warningBorder
                            }`,
                            borderRadius: t.radiusSm,
                            padding: '16px 20px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 16 }}>
                                {summary.failures.length === 0 ? '✓' : '⚠'}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary }}>
                                {summary.failures.length === 0
                                    ? `${summary.successes} sociedad${summary.successes !== 1 ? 'es' : ''} actualizada${summary.successes !== 1 ? 's' : ''} exitosamente`
                                    : `${summary.successes} de ${entidades.length} sociedades actualizadas`}
                            </span>
                        </div>
                        {summary.failures.length > 0 && (
                            <div
                                style={{
                                    fontSize: 13,
                                    color: t.danger,
                                    lineHeight: 1.6,
                                    paddingLeft: 24,
                                    marginTop: 6,
                                }}
                            >
                                Error en: {summary.failures.join(', ')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default CfdiRefreshDashboardComponent
